/**
 * Form Validation Utility using AJV
 * Provides real-time validation for job submission forms
 */

import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import {
    fitJobSchema,
    attributeJobSchema,
    targetMaxJobSchema,
    basicCheckSchema,
    jobSchemaMap,
    ajvConfig,
    customFormats
} from './validationSchemas';

/**
 * Initialize AJV instance with custom configuration
 */
const ajv = new Ajv(ajvConfig);

// Add format validators
addFormats(ajv);

// Add custom format validators
Object.entries(customFormats).forEach(([formatName, formatValidator]) => {
    ajv.addFormat(formatName, formatValidator);
});

/**
 * Compile schemas for faster validation
 */
const compiledSchemas = {
    fit: ajv.compile(fitJobSchema),
    attribute: ajv.compile(attributeJobSchema),
    target_max: ajv.compile(targetMaxJobSchema),
    basicCheck: ajv.compile(basicCheckSchema)
};

/**
 * Validates form data against a specific job type schema
 * @param {string} jobType - The job type (fit, attribute, target_max)
 * @param {object} data - The form data to validate
 * @returns {object} Validation result with isValid flag and errors array
 */
export const validateJobData = (jobType, data) => {
    if (!compiledSchemas[jobType]) {
        console.error(`[validateJobData] Unknown job type: ${jobType}`);
        return {
            isValid: false,
            errors: [
                {
                    message: `Unknown job type: ${jobType}`,
                    type: 'jobType',
                    path: 'job_type'
                }
            ]
        };
    }

    const validate = compiledSchemas[jobType];
    const isValid = validate(data);

    if (isValid) {
        console.log(`[validateJobData] Validation passed for ${jobType}`);
        return {
            isValid: true,
            errors: []
        };
    }

    console.warn(`[validateJobData] Validation failed for ${jobType}`);
    console.warn(`[validateJobData] Data being validated:`, data);
    console.warn(`[validateJobData] AJV errors:`, validate.errors);

    return {
        isValid: false,
        errors: formatValidationErrors(validate.errors)
    };
};

/**
 * Validates a single field against its schema constraints
 * Useful for real-time field validation as user types
 * @param {string} jobType - The job type
 * @param {string} fieldName - The field name to validate
 * @param {*} value - The field value to validate
 * @returns {object} Validation result with isValid flag and error message
 */
export const validateField = (jobType, fieldName, value) => {
    const schema = jobSchemaMap[jobType];
    if (!schema) {
        return {
            isValid: false,
            error: `Unknown job type: ${jobType}`
        };
    }

    const fieldSchema = schema.properties[fieldName];
    if (!fieldSchema) {
        return {
            isValid: true,
            error: null
        };
    }

    // Create a minimal schema for single field validation
    const singleFieldSchema = {
        type: 'object',
        properties: {
            [fieldName]: fieldSchema
        },
        required: schema.required.includes(fieldName) ? [fieldName] : []
    };

    const validate = ajv.compile(singleFieldSchema);
    const isValid = validate({ [fieldName]: value });

    if (isValid) {
        return {
            isValid: true,
            error: null
        };
    }

    // Get the first error message
    const error = validate.errors?.[0];
    const errorMessage = formatSingleError(error, fieldName);

    return {
        isValid: false,
        error: errorMessage
    };
};

/**
 * Validates the basic payload structure
 * Checks that operation and job_type are present
 * @param {object} payload - The payload to validate
 * @returns {object} Validation result with isValid flag and errors array
 */
export const validateBasicPayload = (payload) => {
    const validate = compiledSchemas.basicCheck;
    const isValid = validate(payload);

    if (isValid) {
        return {
            isValid: true,
            errors: []
        };
    }

    return {
        isValid: false,
        errors: formatValidationErrors(validate.errors)
    };
};

/**
 * Validates nested object fields (like fit_args)
 * @param {string} jobType - The job type
 * @param {string} parentField - The parent field name (e.g., 'fit_args')
 * @param {object} nestedData - The nested object data
 * @returns {object} Validation result with isValid flag and errors array
 */
export const validateNestedField = (jobType, parentField, nestedData) => {
    const schema = jobSchemaMap[jobType];
    if (!schema) {
        return {
            isValid: false,
            errors: [{ message: `Unknown job type: ${jobType}` }]
        };
    }

    const nestedSchema = schema.properties[parentField];
    if (!nestedSchema) {
        return {
            isValid: true,
            errors: []
        };
    }

    const validate = ajv.compile(nestedSchema);
    const isValid = validate(nestedData);

    if (isValid) {
        return {
            isValid: true,
            errors: []
        };
    }

    return {
        isValid: false,
        errors: formatValidationErrors(validate.errors, parentField)
    };
};

/**
 * Formats AJV validation errors into a user-friendly structure
 * @param {array} ajvErrors - Array of AJV error objects
 * @param {string} parentPath - Optional parent path for nested fields
 * @returns {array} Formatted error objects
 */
const formatValidationErrors = (ajvErrors, parentPath = '') => {
    if (!ajvErrors || ajvErrors.length === 0) {
        return [];
    }

    return ajvErrors.map((error) => {
        const fieldPath = error.instancePath
            ? error.instancePath.replace(/\//g, '.').substring(1)
            : error.params?.missingProperty || '';

        const fullPath = parentPath
            ? `${parentPath}.${fieldPath}`
            : fieldPath;

        return {
            path: fullPath || error.schemaPath,
            message: formatErrorMessage(error),
            type: error.keyword,
            params: error.params,
            dataPath: error.instancePath
        };
    });
};

/**
 * Formats a single AJV error into a user-friendly message
 * @param {object} error - AJV error object
 * @returns {string} Formatted error message
 */
const formatErrorMessage = (error) => {
    const { keyword, params } = error;

    switch (keyword) {
        case 'required':
            return `${params.missingProperty} is required`;

        case 'type':
            return `must be of type ${params.type}`;

        case 'enum':
            return `must be one of: ${params.allowedValues.join(', ')}`;

        case 'minLength':
            return `must be at least ${params.limit} character(s)`;

        case 'maxLength':
            return `must be at most ${params.limit} character(s)`;

        case 'minimum':
            return `must be at least ${params.limit}`;

        case 'maximum':
            return `must be at most ${params.limit}`;

        case 'minItems':
            return `must have at least ${params.limit} item(s)`;

        case 'maxItems':
            return `must have at most ${params.limit} item(s)`;

        case 'pattern':
            return `must match the required format`;

        case 'format':
            return `must be a valid ${params.format}`;

        case 'additionalProperties':
            return `${params.additionalProperty} is not allowed`;

        case 'const':
            return `must be ${params.allowedValue}`;

        default:
            return error.message || 'Validation failed';
    }
};

/**
 * Formats a single field error into a user-friendly message
 * @param {object} error - AJV error object
 * @param {string} fieldName - The field name
 * @returns {string} Formatted error message
 */
const formatSingleError = (error, fieldName) => {
    if (!error) {
        return null;
    }

    const message = formatErrorMessage(error);
    return message;
};

/**
 * Validates entire form data and returns field-level errors
 * Useful for displaying errors next to each field
 * @param {string} jobType - The job type
 * @param {object} formData - The complete form data
 * @returns {object} Map of field names to error messages
 */
export const getFieldErrors = (jobType, formData) => {
    // Filter form data to only include fields relevant to the current job type
    const schema = jobSchemaMap[jobType];
    if (!schema) {
        console.error(`[getFieldErrors] Unknown job type: ${jobType}`);
        return {};
    }

    // Create filtered form data with only fields from the current job type schema
    const filteredFormData = {};
    Object.keys(schema.properties).forEach((fieldName) => {
        if (fieldName in formData) {
            filteredFormData[fieldName] = formData[fieldName];
        }
    });

    console.log(`[getFieldErrors] Filtered form data for job type ${jobType}:`, filteredFormData);

    const result = validateJobData(jobType, filteredFormData);
    const fieldErrors = {};

    if (!result.isValid) {
        console.log(`[getFieldErrors] Validation failed for job type: ${jobType}`);
        console.log(`[getFieldErrors] Raw validation errors:`, result.errors);

        result.errors.forEach((error) => {
            const fieldName = error.path || error.dataPath;
            if (fieldName && !fieldErrors[fieldName]) {
                fieldErrors[fieldName] = error.message;
                console.log(`[getFieldErrors] Field error - ${fieldName}: ${error.message}`);
            }
        });
    } else {
        console.log(`[getFieldErrors] Validation passed for job type: ${jobType}`);
    }

    return fieldErrors;
};

/**
 * Checks if a form is valid and ready for submission
 * @param {string} jobType - The job type
 * @param {object} formData - The form data to validate
 * @returns {boolean} True if form is valid
 */
export const isFormValid = (jobType, formData) => {
    // Filter form data to only include fields relevant to the current job type
    const schema = jobSchemaMap[jobType];
    if (!schema) {
        console.error(`[isFormValid] Unknown job type: ${jobType}`);
        return false;
    }

    // Create filtered form data with only fields from the current job type schema
    const filteredFormData = {};
    Object.keys(schema.properties).forEach((fieldName) => {
        if (fieldName in formData) {
            filteredFormData[fieldName] = formData[fieldName];
        }
    });

    const result = validateJobData(jobType, filteredFormData);
    console.log(`[isFormValid] Job type: ${jobType}, Valid: ${result.isValid}`);
    if (!result.isValid) {
        console.log(`[isFormValid] Validation errors:`, result.errors);
    }
    return result.isValid;
};

/**
 * Gets all validation errors as a flat array
 * Useful for displaying a summary of all errors
 * @param {string} jobType - The job type
 * @param {object} formData - The form data to validate
 * @returns {array} Array of error objects with path and message
 */
export const getAllValidationErrors = (jobType, formData) => {
    const result = validateJobData(jobType, formData);
    return result.errors;
};

/**
 * Validates and transforms form data for API submission
 * Ensures all required fields are present and valid
 * @param {string} jobType - The job type
 * @param {object} formData - The form data
 * @returns {object} Result with isValid flag, data, and errors
 */
export const validateAndTransformData = (jobType, formData) => {
    // Filter form data to only include fields relevant to the current job type
    const schema = jobSchemaMap[jobType];
    if (!schema) {
        return {
            isValid: false,
            data: null,
            errors: [{ message: `Unknown job type: ${jobType}` }]
        };
    }

    // Create filtered form data with only fields from the current job type schema
    const filteredFormData = {};
    Object.keys(schema.properties).forEach((fieldName) => {
        if (fieldName in formData) {
            filteredFormData[fieldName] = formData[fieldName];
        }
    });

    console.log(`[validateAndTransformData] Filtered form data for job type ${jobType}:`, filteredFormData);

    const validation = validateJobData(jobType, filteredFormData);

    if (!validation.isValid) {
        return {
            isValid: false,
            data: null,
            errors: validation.errors
        };
    }

    // Transform data for API submission
    const transformedData = {
        operation: 'job_param',
        payload: {
            ...filteredFormData,
            job_type: jobType
        }
    };

    return {
        isValid: true,
        data: transformedData,
        errors: []
    };
};

/**
 * Gets the schema for a specific job type
 * Useful for understanding field requirements
 * @param {string} jobType - The job type
 * @returns {object} The JSON Schema for the job type
 */
export const getJobSchema = (jobType) => {
    return jobSchemaMap[jobType] || null;
};

/**
 * Gets required fields for a job type
 * @param {string} jobType - The job type
 * @returns {array} Array of required field names
 */
export const getRequiredFields = (jobType) => {
    const schema = jobSchemaMap[jobType];
    return schema?.required || [];
};

/**
 * Gets all field names for a job type
 * @param {string} jobType - The job type
 * @returns {array} Array of all field names
 */
export const getFieldNames = (jobType) => {
    const schema = jobSchemaMap[jobType];
    return schema ? Object.keys(schema.properties) : [];
};

/**
 * Gets field metadata (type, constraints, etc.)
 * @param {string} jobType - The job type
 * @param {string} fieldName - The field name
 * @returns {object} Field schema object
 */
export const getFieldSchema = (jobType, fieldName) => {
    const schema = jobSchemaMap[jobType];
    return schema?.properties[fieldName] || null;
};

/**
 * Checks if a field is required
 * @param {string} jobType - The job type
 * @param {string} fieldName - The field name
 * @returns {boolean} True if field is required
 */
export const isFieldRequired = (jobType, fieldName) => {
    const schema = jobSchemaMap[jobType];
    return schema?.required?.includes(fieldName) || false;
};

const formValidatorExports = {
    validateJobData,
    validateField,
    validateBasicPayload,
    validateNestedField,
    getFieldErrors,
    isFormValid,
    getAllValidationErrors,
    validateAndTransformData,
    getJobSchema,
    getRequiredFields,
    getFieldNames,
    getFieldSchema,
    isFieldRequired
};

export default formValidatorExports;
